import styled from "@emotion/styled";
import { FC, useMemo } from "react";

import { Info } from "@livechat/design-system-icons";
import { DesignToken } from "@livechat/design-system-react-components";

import { IconsEnum } from "components/icon";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { StyledLink } from "components/links/StyledLink";
import { ReportType } from "constants/reports/report-type";
import { calculateChangeDirection } from "helpers/change-direction";
import { duration } from "helpers/date";
import { ChangeLabel, Chart, SummaryDataRow } from "routes/reports/components";
import { ILast7DaysReportData } from "store/views/reports/interfaces";
import { withVar } from "themes/helpers";

import { EmptyState } from "./ChartEmptyState";
import { getChartOptions, resolveReportsUrl } from "./helpers";
import { AnchorWrapper, Divider, StyledContainer } from "./styled";

const primaryColor = withVar(DesignToken.ContentBasicPrimary);

interface IEmptyStateProps {
  onClick?: () => void;
}

const TicketsEmptyState: FC<IEmptyStateProps> = ({ onClick }) => (
  <EmptyState
    title="No tickets yet"
    icon={Info}
    description={
      <>
        <StyledLink to="/settings/email-addresses" onClick={onClick}>
          Set up email forwarding
        </StyledLink>{" "}
        to start receiving tickets
      </>
    }
  />
);

const FlexRows = styled(Flex)`
  @media (max-width: 1150px) {
    flex-direction: column;
  }
`;

interface IProps {
  isLoading: boolean;
  data?: ILast7DaysReportData;
  className?: string;
  urlResolver?(report: ReportType): string;
  onSummaryClick?(report: ReportType): void;
  onEmptyStateClicked?(): void;
}

const TicketsSummary: FC<IProps> = ({
  isLoading,
  data,
  className,
  urlResolver = resolveReportsUrl,
  onSummaryClick,
  onEmptyStateClicked,
}) => {
  const chartChangeLabel = useMemo(
    () =>
      data && !isNaN(data.newTicketsDifference) ? (
        <ChangeLabel
          value={data.newTicketsDifference}
          labelDifference="from previous 7 days"
          labelEqual="same as previous 7 days"
          positive={data.newTicketsDifference > 0}
          direction={calculateChangeDirection(data.newTicketsDifference)}
        />
      ) : null,
    [data]
  );

  const chartOptions = useMemo(() => data && getChartOptions(data.chart.series, data.chart.labels), [data]);

  const chartLegend = data && [
    {
      value: data.newTickets,
      label: chartChangeLabel,
    },
  ];

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSummaryClick = (report: ReportType) => {
    if (onSummaryClick) {
      onSummaryClick(report);
    }
  };

  const ratingsTotal = data ? data.ticketRatingsBad + data.ticketRatingsOkay + data.ticketRatingsGreat : 0;
  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  const ratedGreat = ratingsTotal ? Math.round((data.ticketRatingsGreat / ratingsTotal) * 100) : 0;
  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  const ratedOkay = ratingsTotal ? Math.round((data.ticketRatingsOkay / ratingsTotal) * 100) : 0;
  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
  const ratedBad = ratingsTotal ? Math.round((data.ticketRatingsBad / ratingsTotal) * 100) : 0;

  return (
    <StyledContainer className={className}>
      <AnchorWrapper
        // @ts-expect-error TS(2322): Type 'string | 0 | undefined' is not assignable to... Remove this comment to see the full error message
        href={data && data.newTickets && urlResolver(ReportType.NewTickets)}
        onClick={() => handleSummaryClick(ReportType.NewTickets)}
      >
        <Block padding="20px">
          <Chart
            title="New tickets"
            loading={isLoading}
            options={chartOptions}
            legend={chartLegend}
            emptyState={data && !data.newTickets ? <TicketsEmptyState onClick={onEmptyStateClicked} /> : undefined}
            disableBreakdown
          />
        </Block>
      </AnchorWrapper>
      {data && (
        <>
          <Divider />
          <Block padding="5px 10px">
            <FlexRows>
              <Flex flex={1}>
                <SummaryDataRow
                  onClick={() => handleSummaryClick(ReportType.SolvedAndClosed)}
                  color={primaryColor}
                  label="Solved tickets"
                  value={data.solvedTickets}
                  emptyState="-"
                  href={urlResolver(ReportType.SolvedAndClosed)}
                />
                <SummaryDataRow
                  onClick={() => handleSummaryClick(ReportType.SolvedAndClosed)}
                  color={primaryColor}
                  label="Closed tickets"
                  value={data.closedTickets}
                  emptyState="-"
                  href={urlResolver(ReportType.SolvedAndClosed)}
                />
                <SummaryDataRow
                  onClick={() => handleSummaryClick(ReportType.TicketFirstResponseTime)}
                  color={primaryColor}
                  label="Avg. response time"
                  value={duration(data.averageFirstResponseTime)}
                  emptyState="-"
                  href={urlResolver(ReportType.TicketFirstResponseTime)}
                />
              </Flex>
              <Flex flex={1}>
                <SummaryDataRow
                  onClick={() => handleSummaryClick(ReportType.TicketRatings)}
                  color={primaryColor}
                  icon={IconsEnum.SmileyHappy}
                  label="Rated great"
                  value={`${ratedGreat}%`}
                  change={data.ticketRatingsGreatDifference}
                  positive={data.ticketRatingsGreatDifference > 0}
                  href={urlResolver(ReportType.TicketRatings)}
                />
                <SummaryDataRow
                  onClick={() => handleSummaryClick(ReportType.TicketRatings)}
                  color={primaryColor}
                  icon={IconsEnum.SmileyOkay}
                  label="Rated okay"
                  value={`${ratedOkay}%`}
                  change={data.ticketRatingsOkayDifference}
                  href={urlResolver(ReportType.TicketRatings)}
                />
                <SummaryDataRow
                  onClick={() => handleSummaryClick(ReportType.TicketRatings)}
                  color={primaryColor}
                  icon={IconsEnum.SmileySad}
                  label="Rated bad"
                  value={`${ratedBad}%`}
                  change={data.ticketRatingsBadDifference}
                  positive={data.ticketRatingsBadDifference < 0}
                  href={urlResolver(ReportType.TicketRatings)}
                />
              </Flex>
            </FlexRows>
          </Block>
        </>
      )}
    </StyledContainer>
  );
};

export default TicketsSummary;
